import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import { useUserStore } from '../../../stores/userStore';
import useAdminDraftGuard from '../useAdminDraftGuard';

const endpoint = '/admin/app/notification-permission-prompt';
const empty = {
  enabled: false,
  titleKo: '알림 설정 안내',
  titleEn: 'Notification Settings',
  bodyKo: '댓글과 팔로우 소식, 새로운 앨범과 나의 음악 기록을 알려드릴게요.',
  bodyEn: 'Get comments, follows, new albums, and updates from your music log.',
  disclosureKo: '이벤트 혜택 알림도 설정에서 별도로 선택할 수 있어요',
  disclosureEn:
    'Event and promotional notifications can be selected separately in notification settings.',
};

const fields = {
  titleKo: 100,
  titleEn: 100,
  bodyKo: 300,
  bodyEn: 300,
  disclosureKo: 240,
  disclosureEn: 240,
};
function record(value) {
  if (
    !value ||
    typeof value.enabled !== 'boolean' ||
    Object.keys(fields).some((key) => typeof value[key] !== 'string')
  )
    throw new Error('설정 응답을 확인하지 못했습니다. 다시 불러와주세요.');
  return {
    enabled: value.enabled,
    ...Object.fromEntries(Object.keys(fields).map((key) => [key, value[key]])),
  };
}
function activePopup(items) {
  if (!Array.isArray(items)) throw new Error('팝업 상태 응답을 확인하지 못했습니다.');
  const now = Date.now();
  return items
    .map((item) => {
      if (
        !item ||
        typeof item.enabled !== 'boolean' ||
        ['startsAt', 'endsAt'].some((key) => item[key] && !Number.isFinite(Date.parse(item[key])))
      )
        throw new Error('팝업 상태 응답을 확인하지 못했습니다.');
      return (
        item.enabled &&
        (!item.startsAt || Date.parse(item.startsAt) <= now) &&
        (!item.endsAt || now < Date.parse(item.endsAt))
      );
    })
    .some(Boolean);
}
function disclosure(value) {
  const trimmed = value.trim();
  const unwrapped =
    trimmed.startsWith('(') && trimmed.endsWith(')') ? trimmed.slice(1, -1).trim() : trimmed;
  return `(${unwrapped.replace(/[.。]\s*$/, '')})`;
}
export default function NotificationPermissionPromptManagement() {
  const user = useUserStore((state) => state.user);
  return <PromptSession key={JSON.stringify([user?.id, user?.role])} />;
}
function PromptSession() {
  const [form, setForm] = useState(empty);
  const [startupPopupEnabled, setStartupPopupEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const pending = useRef(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const draft = useAdminDraftGuard(form, { enabled: !loading && !loadFailed, busy });

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadFailed(false);
    setError('');
    void Promise.all([api.getAuth(endpoint), api.getAuth('/admin/ads/startup-popup/items')])
      .then(([prompt, popups]) => {
        if (!live) return;
        const next = record(prompt.data.data);
        const active = activePopup(popups.data.data);
        setForm(next);
        draft.markClean(next);
        setStartupPopupEnabled(active);
      })
      .catch(() => {
        if (live) {
          setLoadFailed(true);
          setError('알림 권한 안내와 현재 첫 접속 팝업 상태를 불러오지 못했습니다.');
        }
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (key, value) => {
    if (pending.current) return;
    setSaved(false);
    setForm((current) => ({ ...current, [key]: value }));
  };
  const save = async (event) => {
    event.preventDefault();
    if (pending.current || loading || loadFailed) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setSaved(false);
    const payload = Object.fromEntries(
      Object.entries(form).map(([key, value]) =>
        typeof value === 'string' ? [key, value.trim()] : [key, value],
      ),
    );
    try {
      if (
        !payload.titleKo ||
        !payload.titleEn ||
        !payload.bodyKo ||
        !payload.bodyEn ||
        !payload.disclosureKo ||
        !payload.disclosureEn
      )
        throw new Error('한글·영문 제목, 본문, 보조 안내를 모두 입력해주세요.');
      if (Object.entries(fields).some(([key, max]) => payload[key].length > max))
        throw new Error('제목은100자, 본문은300자, 보조 안내는240자 이하로 입력해주세요.');
      const { data } = await api.putAuth(endpoint, payload);
      if (!live.current) return;
      const confirmed = record(data.data);
      setForm(confirmed);
      draft.markClean(confirmed);
      setSaved(true);
    } catch (failure) {
      if (live.current)
        setError(failure.response?.data?.msg || failure.message || '저장하지 못했습니다.');
    } finally {
      pending.current = false;
      if (live.current) setBusy(false);
    }
  };

  if (loading) return <Typography>설정을 불러오는 중…</Typography>;
  if (loadFailed) {
    return (
      <Alert
        severity="error"
        sx={{
          overflowWrap: 'anywhere',
          flexWrap: 'wrap',
          '& .MuiAlert-message': { minWidth: 0, flex: '1 1 100px' },
        }}
        action={<Button onClick={() => setReloadKey((value) => value + 1)}>재시도</Button>}
      >
        {error}
      </Alert>
    );
  }
  return (
    <Stack spacing={2} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Typography variant="h6">알림 권한 안내</Typography>
      <Alert severity="info">
        회원과 비회원 모두 기기의 안내 이력과 알림 상태에 따라 홈 진입 시 표시됩니다. ‘알림 켜기’를
        누르면 기기 상태에 따라 OS 권한을 요청하거나 OS 설정으로 이동합니다. 이미 OS 권한이 있으면
        앱 알림을 켭니다. 이벤트·혜택 알림은 별도로 선택합니다.
      </Alert>
      {form.enabled && startupPopupEnabled && (
        <Alert severity="warning">
          불러온 시점에 첫 접속 팝업도 공개 중입니다. 기기의 표시 조건이 맞으면 팝업 위에 알림
          안내가 표시될 수 있습니다.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Box component="form" aria-label="알림 권한 안내 편집" onSubmit={save}>
        <Stack
          spacing={2}
          component="fieldset"
          disabled={busy}
          sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
        >
          <FormControlLabel
            label="앱에 안내 표시"
            control={
              <Checkbox
                checked={form.enabled}
                onChange={(event) => change('enabled', event.target.checked)}
              />
            }
          />
          <TextField
            required
            label="제목 (한국어)"
            value={form.titleKo}
            inputProps={{ maxLength: 100 }}
            onChange={(event) => change('titleKo', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="본문 (한국어)"
            value={form.bodyKo}
            inputProps={{ maxLength: 300 }}
            onChange={(event) => change('bodyKo', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="보조 안내 (한국어)"
            value={form.disclosureKo}
            inputProps={{ maxLength: 240 }}
            onChange={(event) => change('disclosureKo', event.target.value)}
          />
          <TextField
            required
            label="제목 (영어)"
            value={form.titleEn}
            inputProps={{ maxLength: 100 }}
            onChange={(event) => change('titleEn', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="본문 (영어)"
            value={form.bodyEn}
            inputProps={{ maxLength: 300 }}
            onChange={(event) => change('bodyEn', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="보조 안내 (영어)"
            value={form.disclosureEn}
            inputProps={{ maxLength: 240 }}
            onChange={(event) => change('disclosureEn', event.target.value)}
          />
          <Typography variant="subtitle1">한국어 문구 미리보기</Typography>
          <Typography variant="caption" color="text.secondary">
            앱에서는 기기의 기본 대화상자로 표시되므로 모양과 글자 크기는 기기에 따라 달라집니다.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              maxWidth: 390,
              minWidth: 0,
              borderRadius: 3,
              overflow: 'hidden',
              containerType: 'inline-size',
            }}
          >
            <Stack spacing={2} sx={{ p: 3 }}>
              <Typography fontSize={19} fontWeight={700} lineHeight="26px">
                {form.titleKo}
              </Typography>
              <Typography sx={{ fontSize: 15, lineHeight: '23px', whiteSpace: 'pre-wrap' }}>
                {form.bodyKo}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {disclosure(form.disclosureKo)}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
                '@container (max-width: 240px)': { gridTemplateColumns: '1fr' },
                '& .MuiButton-root': { minWidth: 0, minHeight: 44, overflowWrap: 'anywhere' },
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                type="button"
                tabIndex={-1}
                disableRipple
                sx={{ borderRadius: 0, color: 'text.secondary' }}
              >
                나중에
              </Button>
              <Button type="button" tabIndex={-1} disableRipple sx={{ borderRadius: 0 }}>
                알림 켜기
              </Button>
            </Box>
          </Paper>
          {saved && <Alert severity="success">저장했습니다.</Alert>}
          <Button type="submit" variant="contained">
            {busy ? '저장 중…' : '저장'}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
